import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../../hooks';
import proofkitFilemakerService from '../../../../../services/proofkitFilemakerService';
import { ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Textarea } from '../../../../../components/ui';
import { ZSegments } from '../../../../../schema/Segments';

const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";

export default function SegmentsFormEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    activo: "1",
  });

  useEffect(() => {
    fetchSegment();
  }, [id]);

  const fetchSegment = async () => {
    try {
      setLoading(true);
      const res = await proofkitFilemakerService.getRecord(SEGMENTS_LAYOUT, id);
      const fieldData = res?.data?.fieldData || {};
      
      setFormData({
        nombre: fieldData.nombre || "",
        descripcion: fieldData.descripcion || "",
        activo: fieldData.activo === undefined || fieldData.activo === null || fieldData.activo === "" ? "" : String(fieldData.activo),
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo cargar el segmento",
        variant: "destructive",
      });
      navigate("/campanas/segments");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payloadBase = {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
      };
      const payload = ZSegments
        .pick({ nombre: true, descripcion: true, activo: true })
        .partial()
        .parse(
          formData.activo !== ""
            ? { ...payloadBase, activo: Number(formData.activo) }
            : payloadBase
        );

      await proofkitFilemakerService.updateRecord(SEGMENTS_LAYOUT, id, payload);
      toast({
        title: "Éxito",
        description: "Segmento actualizado correctamente",
      });
      navigate("/campanas/segments");
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el segmento",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando segmento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate("/campanas/segments")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Editar Segmento</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del Segmento</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="activo">Activo</Label>
                <select
                  id="activo"
                  name="activo"
                  value={formData.activo}
                  onChange={handleChange}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Sin dato</option>
                  <option value="1">Sí</option>
                  <option value="0">No</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/campanas/segments")}>
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
